import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, ArrowRight, Check, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Seo from "@/components/common/Seo";
import PageHeader from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BRAND } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  "Fractional CIO", "Executive Advisory", "Technology Strategy",
  "Digital Transformation", "AI Strategy", "ERP & SAP Advisory", "Other",
];
const ROLES = ["Chairman", "CEO", "Managing Director", "Founder", "Board Member", "PE Partner", "CIO / CTO", "Other"];

const PROMISES = [
  "A 30-minute confidential conversation",
  "A candid read on your situation, no pitch",
  "Two or three moves worth making next",
];

export default function BookSession() {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", topic: "", message: "" });
  const [date, setDate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        type: "strategy_session",
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
      });
      setDone(true);
      toast.success("Request received. Aftab's team will be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo
        title="Book Executive Strategy Session"
        description="Book a confidential executive strategy session with Aftab Azad, Executive Technology Advisor and Fractional CIO to CEOs, boards, growing businesses and family-owned enterprises across India, the UAE and the Gulf Region."
        path="/book"
        keywords={["Book Executive Strategy Session", "Executive Consultation", "Executive Technology Advisor", "Fractional CIO India", "Fractional CIO UAE", "Fractional CIO Gulf", "Virtual CIO"]}
      />
      <PageHeader
        overline="Book a strategy session"
        titleLines={["A conversation", "worth your time."]}
        subtitle="Tell us a little about your priorities. We'll arrange a confidential session at a time that suits your schedule."
        testId="book-header"
      />

      <section data-testid="book-section" className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-navy">What to expect</p>
              <h2 className="mt-6 font-heading text-3xl font-light leading-tight tracking-tight text-navy md:text-4xl">
                Focused, confidential, <span className="font-black">no obligation.</span>
              </h2>
              <ul className="mt-8 space-y-4">
                {PROMISES.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-navy text-white">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span className="font-body text-slate-700">{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10 space-y-4 border-t border-slate-200 pt-8">
                <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 font-body text-slate-700 hover:text-navy">
                  <Mail className="h-5 w-5 text-navy" strokeWidth={1.5} /> {BRAND.email}
                </a>
                <div className="flex items-center gap-3 font-body text-slate-700">
                  <Clock className="h-5 w-5 text-navy" strokeWidth={1.5} /> Response within one business day
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div data-testid="book-success" className="flex h-full flex-col items-start justify-center rounded-[1.75rem] border border-slate-200 bg-surface p-12">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-navy text-white">
                  <Check className="h-8 w-8" strokeWidth={2} />
                </div>
                <h3 className="mt-6 font-heading text-3xl font-bold tracking-tight text-navy">Thank you, {form.name.split(" ")[0] || "there"}.</h3>
                <p className="mt-3 max-w-md font-body text-lg leading-relaxed text-slate-600">
                  Your request has been received. Aftab&rsquo;s team will reach out within one business day to confirm your session.
                </p>
              </div>
            ) : (
              <form data-testid="book-form" onSubmit={submit} className="rounded-[1.75rem] border border-slate-200 bg-surface p-8 md:p-10">
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Full name" htmlFor="name">
                    <Input id="name" data-testid="book-name" required value={form.name} onChange={set("name")} placeholder="Jane Executive" className="bg-white" />
                  </Field>
                  <Field label="Work email" htmlFor="email">
                    <Input id="email" data-testid="book-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@company.com" className="bg-white" />
                  </Field>
                  <Field label="Company" htmlFor="company">
                    <Input id="company" data-testid="book-company" value={form.company} onChange={set("company")} placeholder="Company name" className="bg-white" />
                  </Field>
                  <Field label="Your role" htmlFor="role">
                    <Select value={form.role} onValueChange={set("role")}>
                      <SelectTrigger data-testid="book-role" className="bg-white"><SelectValue placeholder="Select role" /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Topic of interest" htmlFor="topic">
                    <Select value={form.topic} onValueChange={set("topic")}>
                      <SelectTrigger data-testid="book-topic" className="bg-white"><SelectValue placeholder="Select topic" /></SelectTrigger>
                      <SelectContent>{TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Preferred date" htmlFor="date">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" data-testid="book-date-trigger" className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-white px-3 py-2 text-left font-body text-sm text-slate-700">
                          <CalendarIcon className="h-4 w-4 text-navy" />
                          {date ? format(date, "PPP") : <span className="text-slate-400">Pick a date</span>}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </Field>
                </div>
                <Field label="What would you like to discuss?" htmlFor="message" className="mt-6">
                  <Textarea id="message" data-testid="book-message" rows={4} value={form.message} onChange={set("message")} placeholder="A few lines on your priorities or challenge…" className="bg-white" />
                </Field>
                <button
                  data-testid="book-submit"
                  disabled={busy}
                  className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors hover:bg-navy disabled:opacity-60 sm:w-auto"
                >
                  {busy ? "Sending…" : "Request my session"}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, htmlFor, children, className = "" }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="font-body text-sm font-medium text-navy">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
